// AiOpsCoach Communications Engine - Comprehensive Mock Data
// This file contains all mock data for the frontend application

import type { 
  User, 
  Hook, 
  Section, 
  Signature, 
  Component,
  Draft, 
  Brokerage, 
  Campaign, 
  AnalyticsDashboard, 
  ConversionFunnel, 
  ABTestResults,
  RealTimeMetrics,
  PerformanceMetric,
  Task,
  UserPermission
} from '../types';

// User Authentication Mock Data
export const mockUsers: User[] = [
  {
    id: 'user-001',
    name: 'Shawn',
    email: 'Shawn@aiopscoach.com',
    role: 'admin' as const,
    roles: ['admin'], // Add roles array
    avatar: '/avatars/Shawn.jpg',
    accessToken: 'mock-jwt-token-123',
    refreshToken: 'mock-refresh-token-456',
    permissions: [
      { id: 'perm-1', name: 'Dashboard Access', description: 'View dashboard', resource: 'dashboard', granted: true },
      { id: 'perm-2', name: 'Define Access', description: 'Manage components', resource: 'define', granted: true },
      { id: 'perm-3', name: 'Draft Access', description: 'Create drafts', resource: 'draft', granted: true },
      { id: 'perm-4', name: 'Send Access', description: 'Send campaigns', resource: 'send', granted: true },
      { id: 'perm-5', name: 'Analytics Access', description: 'View analytics', resource: 'comms_analytics', granted: true },
      { id: 'perm-6', name: 'Cap Tables Access', description: 'View cap tables', resource: 'cap_tables', granted: true },
      { id: 'perm-7', name: 'P&L Access', description: 'View profit & loss', resource: 'profit_loss', granted: true },
      { id: 'perm-8', name: 'Projects Access', description: 'Manage projects', resource: 'projects', granted: true },
      { id: 'perm-9', name: 'User Admin', description: 'Manage users', resource: 'users_admin', granted: true }
    ],
    createdAt: '2024-01-15T10:30:00Z',
    lastLogin: '2025-08-10T09:15:00Z'
  },
  {
    id: 'user-002',
    name: 'Will',
    email: 'Will@aiopscoach.com',
    role: 'user' as const,
    roles: ['user'], // Add roles array
    avatar: '/avatars/Will.jpg',
    accessToken: 'mock-jwt-token-789',
    refreshToken: 'mock-refresh-token-101',
    permissions: [
      { id: 'perm-1', name: 'Dashboard Access', description: 'View dashboard', resource: 'dashboard', granted: true },
      { id: 'perm-2', name: 'Define Access', description: 'Manage components', resource: 'define', granted: true },
      { id: 'perm-3', name: 'Draft Access', description: 'Create drafts', resource: 'draft', granted: true },
      { id: 'perm-5', name: 'Analytics Access', description: 'View analytics', resource: 'comms_analytics', granted: true },
      { id: 'perm-8', name: 'Projects Access', description: 'Manage projects', resource: 'projects', granted: true }
    ],
    createdAt: '2024-01-15T10:30:00Z',
    lastLogin: '2025-08-10T09:15:00Z'
  },
  // Add a user with multiple roles for testing
  {
    id: 'user-003',
    name: 'Test Manager',
    email: 'testmanager@aiopscoach.com',
    role: 'manager' as const, // Legacy role still works
    roles: ['admin', 'manager', 'user'], // Multiple roles for testing
    avatar: '/avatars/testmanager.jpg',
    accessToken: 'mock-jwt-token-999',
    refreshToken: 'mock-refresh-token-999',
    permissions: [
      { id: 'perm-1', name: 'Dashboard Access', description: 'View dashboard', resource: 'dashboard', granted: true },
      { id: 'perm-2', name: 'Define Access', description: 'Manage components', resource: 'define', granted: true },
      { id: 'perm-3', name: 'Draft Access', description: 'Create drafts', resource: 'draft', granted: true },
      { id: 'perm-4', name: 'Send Access', description: 'Send campaigns', resource: 'send', granted: true },
      { id: 'perm-5', name: 'Analytics Access', description: 'View analytics', resource: 'comms_analytics', granted: true },
      { id: 'perm-6', name: 'Cap Tables Access', description: 'View cap tables', resource: 'cap_tables', granted: true },
      { id: 'perm-7', name: 'P&L Access', description: 'View profit & loss', resource: 'profit_loss', granted: true },
      { id: 'perm-8', name: 'Projects Access', description: 'Manage projects', resource: 'projects', granted: true }
    ],
    createdAt: '2024-01-15T10:30:00Z',
    lastLogin: '2025-08-10T09:15:00Z'
  }
];

// Component Library Mock Data
export const mockHooks = [
  {
    id: 'hook-001',
    content: 'Are you tired of missing out on prime real estate opportunities because your listing process takes too long?',
    tier: 1,
    performanceScore: 92,
    thumbStopRate: 87,
    tags: ['real-estate', 'urgency', 'problem-focused'],
    usageCount: 245,
    createdAt: '2024-10-15T14:30:00Z',
    updatedAt: '2025-08-01T11:20:00Z',
    type: 'hook',
    metadata: {
      readabilityScore: 85,
      engagementMetrics: {
        clickThroughRate: 23.4,
        conversionRate: 8.9,
        bounceRate: 12.3
      }
    }
  },
  {
    id: 'hook-002',
    content: 'What if I told you there\'s a way to double your client response rate in just 30 days?',
    tier: 2,
    performanceScore: 88,
    thumbStopRate: 82,
    tags: ['promise', 'curiosity', 'results-driven'],
    usageCount: 198,
    createdAt: '2024-11-02T09:45:00Z',
    updatedAt: '2025-07-28T16:10:00Z',
    type: 'hook',
    metadata: {
      readabilityScore: 90,
      engagementMetrics: {
        clickThroughRate: 21.7,
        conversionRate: 7.2,
        bounceRate: 14.8
      }
    }
  },
  {
    id: 'hook-003',
    content: 'Last week, a small brokerage in Austin closed 40% more deals using our new client engagement system.',
    tier: 1,
    performanceScore: 94,
    thumbStopRate: 89,
    tags: ['social-proof', 'specific-results', 'location-based'],
    usageCount: 312,
    createdAt: '2024-09-20T13:15:00Z',
    updatedAt: '2025-08-05T08:30:00Z',
    type: 'hook',
    metadata: {
      readabilityScore: 88,
      engagementMetrics: {
        clickThroughRate: 26.1,
        conversionRate: 9.8,
        bounceRate: 10.2
      }
    }
  }
];

export const mockSections = [
  {
    id: 'section-001',
    content: 'The real estate market moves fast, and so should your listing process. Every hour your property sits unlisted is potential revenue walking out the door. Our streamlined system cuts listing time by 75%, getting your properties in front of buyers while the market is hot.',
    type: 'problem',
    citations: [
      {
        id: 'cite-001',
        source: 'National Association of Realtors Market Study 2024',
        url: 'https://example.com/nar-study',
        credibilityScore: 95,
        datePublished: '2024-06-15T00:00:00Z'
      }
    ],
    readabilityScore: 82,
    engagementMetrics: {
      timeOnPage: 45.2,
      scrollDepth: 78.5,
      shareRate: 3.2
    },
    performanceScore: 87,
    usageCount: 156,
    tags: ['urgency', 'efficiency', 'market-timing'],
    createdAt: '2024-10-08T12:00:00Z',
    updatedAt: '2025-07-30T14:45:00Z'
  },
  {
    id: 'section-002',
    content: 'Here\'s exactly what happens when you switch to our platform: Week 1 - Complete system setup and team training. Week 2 - Launch your first optimized campaigns. Week 3 - See 40% increase in qualified leads. Week 4 - Close your first deal 60% faster than industry average.',
    type: 'solution',
    citations: [],
    readabilityScore: 85,
    engagementMetrics: {
      timeOnPage: 52.8,
      scrollDepth: 82.1,
      shareRate: 5.7
    },
    performanceScore: 91,
    usageCount: 203,
    tags: ['step-by-step', 'timeline', 'specific-results'],
    createdAt: '2024-11-12T15:30:00Z',
    updatedAt: '2025-08-02T10:15:00Z'
  },
  {
    id: 'section-003',
    content: 'Don\'t just take our word for it. Jennifer Martinez from Phoenix Realty increased her transaction volume by 85% in her first quarter. "I was skeptical at first, but the results speak for themselves. My clients are happier, my team is more efficient, and my bottom line has never looked better."',
    type: 'social_proof',
    citations: [
      {
        id: 'cite-002',
        source: 'Client Testimonial - Phoenix Realty',
        url: null,
        credibilityScore: 88,
        datePublished: '2025-02-10T00:00:00Z'
      }
    ],
    readabilityScore: 79,
    engagementMetrics: {
      timeOnPage: 38.7,
      scrollDepth: 75.3,
      shareRate: 8.9
    },
    performanceScore: 89,
    usageCount: 174,
    tags: ['testimonial', 'specific-results', 'credibility'],
    createdAt: '2024-12-05T11:20:00Z',
    updatedAt: '2025-07-25T13:40:00Z'
  }
];

export const mockSignatures = [
  {
    id: 'signature-001',
    content: `Best regards,

Shawn
CEO, Founder
AiOpsCoach
📧 Shawn@aiopscoach.com
📱 (555) 123-4567
🌐 www.aiopscoach.com

P.S. Ready to see how top brokerages are staying ahead? Book a 15-minute demo this week and get our exclusive "Real Estate Growth Playbook" absolutely free.`,
    type: 'signature',
    performanceScore: 78,
    usageCount: 89,
    tags: ['professional', 'contact-info', 'call-to-action'],
    createdAt: '2024-09-15T16:00:00Z',
    updatedAt: '2025-07-20T09:30:00Z'
  }
];

// Draft Management Mock Data
export const mockDrafts = [
  {
    id: 'draft-001',
    name: 'Q4 High-Performance Outreach',
    status: 'draft',
    hook: mockHooks[0],
    sections: [
      {
        id: 'ds-001',
        component: mockSections[0],
        order: 1,
        customizations: {}
      },
      {
        id: 'ds-002',
        component: mockSections[1],
        order: 2,
        customizations: {}
      }
    ],
    signature: mockSignatures[0],
    subjectLine: 'Missing out on prime opportunities? Here\'s the fix...',
    previewText: 'Cut your listing time by 75% and never miss another hot market opportunity',
    createdAt: '2025-08-05T10:15:00Z',
    updatedAt: '2025-08-10T14:30:00Z',
    createdBy: 'user-001',
    tags: ['q4-campaign', 'high-priority'],
    estimatedPerformance: {
      openRate: 28.5,
      clickRate: 12.3,
      conversionRate: 4.7
    }
  },
  {
    id: 'draft-002',
    name: 'Austin Market Expansion',
    status: 'scheduled',
    hook: mockHooks[2],
    sections: [
      {
        id: 'ds-003',
        component: mockSections[2],
        order: 1,
        customizations: {}
      }
    ],
    signature: mockSignatures[0],
    subjectLine: 'How Austin brokerages are closing 40% more deals',
    previewText: 'See the exact system that\'s transforming the Austin real estate market',
    scheduledDate: '2025-08-15T09:00:00Z',
    createdAt: '2025-08-02T11:45:00Z',
    updatedAt: '2025-08-08T16:20:00Z',
    createdBy: 'user-001',
    tags: ['austin', 'geo-targeted'],
    estimatedPerformance: {
      openRate: 32.1,
      clickRate: 15.7,
      conversionRate: 6.2
    }
  },
  {
    id: 'draft-003',
    name: 'Follow-up Sequence #1',
    status: 'sent',
    hook: mockHooks[1],
    sections: [
      {
        id: 'ds-004',
        component: mockSections[1],
        order: 1,
        customizations: {}
      }
    ],
    signature: mockSignatures[0],
    subjectLine: 'Double your response rate (30-day challenge)',
    previewText: 'Join 500+ brokerages already seeing incredible results',
    sentDate: '2025-07-25T08:30:00Z',
    createdAt: '2025-07-20T13:15:00Z',
    updatedAt: '2025-07-25T08:30:00Z',
    createdBy: 'user-001',
    tags: ['follow-up', 'challenge'],
    actualPerformance: {
      openRate: 29.8,
      clickRate: 13.4,
      conversionRate: 5.1
    }
  }
];

// Brokerage Data Mock Data
export const mockBrokerages: Brokerage[] = [
  {
    id: 'brokerage-001',
    name: 'Elite Realty Partners',
    vertical: 'Commercial' as const,
    agentCount: 125,
    revenue: 45000000,
    state: 'TX',
    city: 'Dallas',
    marketShare: 12.5,
    contactInfo: {
      email: 'partnerships@eliterealty.com',
      phone: '(214) 555-0123',
      address: '1200 Main St, Dallas, TX 75201'
    },
    lastContacted: '2025-06-15T10:30:00Z',
    engagement: {
      emailOpens: 8,
      linkClicks: 3,
      responseRate: 15.5
    },
    tags: ['high-value', 'commercial-focus', 'texas'],
    notes: 'Interested in enterprise solutions, quarterly budget cycle'
  },
  {
    id: 'brokerage-002',
    name: 'Sunshine Properties Inc.',
    vertical: 'Residential' as const,
    agentCount: 67,
    revenue: 18500000,
    state: 'FL',
    city: 'Miami',
    marketShare: 8.3,
    contactInfo: {
      email: 'info@sunshineprops.com',
      phone: '(305) 555-0199',
      address: '850 Brickell Ave, Miami, FL 33131'
    },
    lastContacted: '2025-07-20T14:45:00Z',
    engagement: {
      emailOpens: 12,
      linkClicks: 5,
      responseRate: 22.1
    },
    tags: ['residential', 'growing', 'florida'],
    notes: 'Rapid growth phase, looking for scalable solutions'
  },
  {
    id: 'brokerage-003',
    name: 'Mountain View Realty',
    vertical: 'Luxury' as const,
    agentCount: 34,
    revenue: 28000000,
    state: 'CO',
    city: 'Denver',
    marketShare: 15.7,
    contactInfo: {
      email: 'contact@mountainviewre.com',
      phone: '(303) 555-0167',
      address: '1600 17th St, Denver, CO 80202'
    },
    lastContacted: '2025-05-10T09:20:00Z',
    engagement: {
      emailOpens: 6,
      linkClicks: 2,
      responseRate: 8.9
    },
    tags: ['luxury', 'boutique', 'colorado'],
    notes: 'Boutique firm, focuses on high-end properties'
  }
];

// Campaign Performance Mock Data
export const mockCampaigns: Campaign[] = [
  {
    id: 'campaign-001',
    name: 'Q3 Commercial Outreach',
    status: 'completed' as const,
    draftId: 'draft-003',
    recipients: 245,
    sentDate: '2025-07-25T08:30:00Z',
    performance: {
      sent: 245,
      delivered: 238,
      opened: 71,
      clicked: 32,
      converted: 12,
      bounced: 7,
      unsubscribed: 2,
      openRate: 29.8,
      clickRate: 13.4,
      conversionRate: 5.1,
      bounceRate: 2.9
    },
    segments: ['commercial', 'high-revenue'],
    abTest: {
      isActive: false,
      variants: [],
      winner: null
    }
  },
  {
    id: 'campaign-002',
    name: 'Austin Market Expansion',
    status: 'active' as const,
    draftId: 'draft-002',
    recipients: 89,
    sentDate: '2025-08-15T09:00:00Z',
    performance: {
      sent: 89,
      delivered: 87,
      opened: 28,
      clicked: 15,
      converted: 5,
      bounced: 2,
      unsubscribed: 0,
      openRate: 32.1,
      clickRate: 17.2,
      conversionRate: 5.7,
      bounceRate: 2.2
    },
    segments: ['austin', 'residential'],
    abTest: {
      isActive: true,
      variants: [
        {
          id: 'variant-a',
          name: 'Original Subject Line',
          percentage: 50,
          performance: {
            sent: 44,
            opened: 14,
            clicked: 8,
            converted: 3
          }
        },
        {
          id: 'variant-b',
          name: 'Urgency Subject Line',
          percentage: 50,
          performance: {
            sent: 45,
            opened: 14,
            clicked: 7,
            converted: 2
          }
        }
      ],
      winner: null
    }
  }
];

// Analytics Mock Data
export const mockAnalytics = {
  dashboard: {
    timeRange: '7d',
    totalCampaigns: 24,
    activeCampaigns: 3,
    totalSent: 3420,
    avgOpenRate: 31.2,
    avgClickRate: 14.8,
    totalConversions: 187,
    conversionGrowth: 12.5,
    topPerformingHook: mockHooks[2],
    recentActivity: [
      {
        id: 'activity-001',
        type: 'campaign_sent',
        description: 'Austin Market Expansion campaign sent to 89 recipients',
        timestamp: '2025-08-15T09:00:00Z',
        campaignId: 'campaign-002'
      },
      {
        id: 'activity-002',
        type: 'high_conversion',
        description: 'Q3 Commercial Outreach achieved 5.1% conversion rate',
        timestamp: '2025-08-14T16:30:00Z',
        campaignId: 'campaign-001'
      }
    ]
  },
  conversionFunnel: {
    campaignId: 'campaign-001',
    stages: [
      {
        name: 'Sent',
        count: 245,
        rate: 100,
        dropoffRate: 0
      },
      {
        name: 'Delivered',
        count: 238,
        rate: 97.1,
        dropoffRate: 2.9
      },
      {
        name: 'Opened',
        count: 71,
        rate: 29.8,
        dropoffRate: 67.2
      },
      {
        name: 'Clicked',
        count: 32,
        rate: 45.1,
        dropoffRate: 54.9
      },
      {
        name: 'Converted',
        count: 12,
        rate: 37.5,
        dropoffRate: 62.5
      }
    ],
    segmentBreakdown: [
      {
        segment: 'Commercial',
        funnel: [245, 238, 71, 32, 12],
        conversionRate: 4.9
      },
      {
        segment: 'High Revenue',
        funnel: [156, 152, 48, 22, 9],
        conversionRate: 5.8
      }
    ]
  },
  performanceMetrics: [
    {
      date: '2025-08-01',
      openRate: 28.5,
      clickRate: 12.3,
      conversionRate: 4.2,
      sent: 145
    },
    {
      date: '2025-08-02',
      openRate: 31.2,
      clickRate: 14.1,
      conversionRate: 5.1,
      sent: 189
    },
    {
      date: '2025-08-03',
      openRate: 29.8,
      clickRate: 13.7,
      conversionRate: 4.8,
      sent: 167
    },
    {
      date: '2025-08-04',
      openRate: 33.1,
      clickRate: 15.9,
      conversionRate: 6.2,
      sent: 203
    },
    {
      date: '2025-08-05',
      openRate: 30.7,
      clickRate: 14.5,
      conversionRate: 5.3,
      sent: 178
    }
  ]
};

// A/B Test Results Mock Data
export const mockABTestResults = {
  campaignId: 'campaign-002',
  testName: 'Subject Line Optimization',
  startDate: '2025-08-15T09:00:00Z',
  endDate: null,
  status: 'running',
  confidenceLevel: 95,
  variants: [
    {
      id: 'variant-a',
      name: 'How Austin brokerages are closing 40% more deals',
      type: 'control',
      trafficAllocation: 50,
      metrics: {
        sent: 44,
        delivered: 43,
        opened: 14,
        clicked: 8,
        converted: 3,
        openRate: 32.6,
        clickRate: 18.6,
        conversionRate: 7.0
      }
    },
    {
      id: 'variant-b',
      name: 'URGENT: Austin real estate secret (closing tomorrow)',
      type: 'treatment',
      trafficAllocation: 50,
      metrics: {
        sent: 45,
        delivered: 44,
        opened: 14,
        clicked: 7,
        converted: 2,
        openRate: 31.8,
        clickRate: 15.9,
        conversionRate: 4.5
      }
    }
  ],
  statisticalSignificance: {
    isSignificant: false,
    pValue: 0.234,
    chiSquare: 1.42,
    winner: null,
    improvementPercent: 0,
    confidence: 0
  },
  recommendations: [
    'Test is still running. Need more data for statistical significance.',
    'Current sample size suggests control is performing better.',
    'Consider running test for at least 1 more week.'
  ]
};

// Real-time Metrics Mock Data (for WebSocket simulation)
export const mockRealTimeMetrics = {
  activeCampaigns: 3,
  totalSent: 3420,
  avgOpenRate: 31.2,
  totalConversions: 187,
  liveUpdates: [
    {
      timestamp: new Date().toISOString(),
      type: 'email_opened',
      campaignId: 'campaign-002',
      data: { recipient: 'Elite Realty Partners' }
    },
    {
      timestamp: new Date(Date.now() - 300000).toISOString(),
      type: 'conversion',
      campaignId: 'campaign-002',
      data: { recipient: 'Mountain View Realty', value: '$12,500' }
    }
  ]
};

// Task Management Mock Data
export const mockTasks: Task[] = [
  // Shawn's Tasks (user-001)
  {
    id: 'task-001',
    title: 'Implement user authentication system',
    description: 'Set up JWT-based authentication system with refresh tokens and secure session management for the AiOpsCoach platform',
    createDate: '2025-08-10T09:00:00Z',
    taskStatus: 'In Progress',
    assignedUserId: 'user-001',
    createdBy: 'user-001',
    lastModified: '2025-08-15T14:30:00Z',
    lastModifiedBy: 'user-001',
    tags: ['backend', 'security', 'authentication', 'urgent']
  },
  {
    id: 'task-002',
    title: 'Design responsive dashboard layout',
    description: 'Create a responsive dashboard with analytics cards and interactive charts for mobile and desktop',
    createDate: '2025-08-12T10:15:00Z',
    taskStatus: 'Complete',
    completeDate: '2025-08-14T16:45:00Z',
    assignedUserId: 'user-001',
    createdBy: 'user-001',
    lastModified: '2025-08-14T16:45:00Z',
    lastModifiedBy: 'user-001',
    tags: ['frontend', 'ui/ux', 'responsive', 'dashboard']
  },
  {
    id: 'task-003',
    title: 'Optimize database performance',
    description: 'Review and optimize database queries, add indexes, and implement caching strategies for improved response times',
    createDate: '2025-08-05T11:30:00Z',
    taskStatus: 'In Progress',
    assignedUserId: 'user-001',
    createdBy: 'user-001',
    lastModified: '2025-08-13T09:15:00Z',
    lastModifiedBy: 'user-001',
    tags: ['backend', 'database', 'performance', 'optimization']
  },
  {
    id: 'task-004',
    title: 'Implement A/B testing framework',
    description: 'Build framework for testing different email variants and measuring conversion rates',
    createDate: '2025-08-09T13:10:00Z',
    taskStatus: 'Complete',
    completeDate: '2025-08-15T17:30:00Z',
    assignedUserId: 'user-001',
    createdBy: 'user-001',
    lastModified: '2025-08-15T17:30:00Z',
    lastModifiedBy: 'user-001',
    tags: ['backend', 'testing', 'analytics', 'feature']
  },
  {
    id: 'task-005',
    title: 'Mobile app prototype development',
    description: 'Create interactive prototype for mobile application with core features and user flows',
    createDate: '2025-08-06T12:00:00Z',
    taskStatus: 'In Progress',
    assignedUserId: 'user-001',
    createdBy: 'user-001',
    lastModified: '2025-08-14T11:20:00Z',
    lastModifiedBy: 'user-001',
    tags: ['mobile', 'prototype', 'ui/ux', 'frontend']
  },
  {
    id: 'task-006',
    title: 'Code review automation',
    description: 'Set up automated code review process with GitHub Actions and quality gates',
    createDate: '2025-07-28T14:00:00Z',
    taskStatus: 'Not Started',
    assignedUserId: 'user-001',
    createdBy: 'user-001',
    lastModified: '2025-07-28T14:00:00Z',
    lastModifiedBy: 'user-001',
    tags: ['devops', 'automation', 'quality-assurance', 'github']
  },
  {
    id: 'task-007',
    title: 'Security audit and penetration testing for Q3',
    description: 'Comprehensive security assessment including vulnerability scanning, penetration testing, and security policy review to ensure compliance with industry standards',
    createDate: '2025-07-15T08:30:00Z',
    taskStatus: 'Will Not Do',
    completeDate: '2025-08-01T10:00:00Z',
    assignedUserId: 'user-001',
    createdBy: 'user-001',
    lastModified: '2025-08-01T10:00:00Z',
    lastModifiedBy: 'user-001',
    tags: ['security', 'audit', 'compliance', 'testing']
  },
  
  // Will's Tasks (user-002)
  {
    id: 'task-008',
    title: 'Set up email campaign automation',
    description: 'Implement automated email sequences with triggers and personalization for lead nurturing',
    createDate: '2025-08-08T14:20:00Z',
    taskStatus: 'In Progress',
    assignedUserId: 'user-002',
    createdBy: 'user-001',
    lastModified: '2025-08-15T16:10:00Z',
    lastModifiedBy: 'user-002',
    tags: ['marketing', 'automation', 'email', 'backend']
  },
  {
    id: 'task-009',
    title: 'Create API documentation',
    description: 'Write comprehensive API documentation with examples and interactive playground using OpenAPI/Swagger',
    createDate: '2025-08-07T08:45:00Z',
    taskStatus: 'Complete',
    completeDate: '2025-08-14T15:20:00Z',
    assignedUserId: 'user-002',
    createdBy: 'user-001',
    lastModified: '2025-08-14T15:20:00Z',
    lastModifiedBy: 'user-002',
    tags: ['documentation', 'api', 'backend', 'swagger']
  },
  {
    id: 'task-010',
    title: 'Setup monitoring and alerting',
    description: 'Configure application monitoring, error tracking, and automated alerts for system health',
    createDate: '2025-08-11T16:25:00Z',
    taskStatus: 'Not Started',
    assignedUserId: 'user-002',
    createdBy: 'user-001',
    lastModified: '2025-08-11T16:25:00Z',
    lastModifiedBy: 'user-001',
    tags: ['devops', 'monitoring', 'alerting', 'infrastructure']
  },
  {
    id: 'task-011',
    title: 'Content management system integration',
    description: 'Integrate CMS for dynamic content updates and blog management functionality',
    createDate: '2025-07-25T11:00:00Z',
    taskStatus: 'Complete',
    completeDate: '2025-08-10T14:30:00Z',
    assignedUserId: 'user-002',
    createdBy: 'user-002',
    lastModified: '2025-08-10T14:30:00Z',
    lastModifiedBy: 'user-002',
    tags: ['cms', 'integration', 'content', 'backend']
  },
  {
    id: 'task-012',
    title: 'Social media analytics dashboard',
    description: 'Build comprehensive social media analytics dashboard with engagement metrics, reach analysis, and ROI tracking',
    createDate: '2025-08-03T09:15:00Z',
    taskStatus: 'In Progress',
    assignedUserId: 'user-002',
    createdBy: 'user-002',
    lastModified: '2025-08-16T10:45:00Z',
    lastModifiedBy: 'user-002',
    tags: ['analytics', 'dashboard', 'social-media', 'frontend']
  },
  {
    id: 'task-013',
    title: 'User onboarding flow redesign',
    description: 'Redesign the user onboarding experience to improve conversion rates and reduce drop-off',
    createDate: '2025-08-01T13:20:00Z',
    taskStatus: 'Not Started',
    assignedUserId: 'user-002',
    createdBy: 'user-001',
    lastModified: '2025-08-01T13:20:00Z',
    lastModifiedBy: 'user-001',
    tags: ['ui/ux', 'onboarding', 'conversion', 'frontend']
  },
  {
    id: 'task-014',
    title: 'Legacy system migration planning',
    description: 'Plan and document the migration strategy for legacy customer data to the new platform architecture',
    createDate: '2025-07-20T10:00:00Z',
    taskStatus: 'Will Not Do',
    completeDate: '2025-08-05T16:00:00Z',
    assignedUserId: 'user-002',
    createdBy: 'user-001',
    lastModified: '2025-08-05T16:00:00Z',
    lastModifiedBy: 'user-002',
    tags: ['migration', 'legacy', 'planning', 'documentation']
  },
  {
    id: 'task-015',
    title: 'Chat',
    description: 'Quick test task for UI truncation and short title handling',
    createDate: '2025-08-16T08:00:00Z',
    taskStatus: 'Not Started',
    assignedUserId: 'user-002',
    createdBy: 'user-002',
    lastModified: '2025-08-16T08:00:00Z',
    lastModifiedBy: 'user-002',
    tags: ['testing', 'ui']
  },

  // Mixed assignments for testing
  {
    id: 'task-016',
    title: 'Performance optimization and caching strategy implementation',
    description: 'Implement comprehensive performance optimization including CDN setup, browser caching, lazy loading, and database query optimization for improved user experience',
    createDate: '2025-07-18T15:45:00Z',
    taskStatus: 'Complete',
    completeDate: '2025-08-12T11:30:00Z',
    assignedUserId: 'user-001',
    createdBy: 'user-002',
    lastModified: '2025-08-12T11:30:00Z',
    lastModifiedBy: 'user-001',
    tags: ['performance', 'optimization', 'caching', 'backend', 'infrastructure']
  }
];

// Utility functions for mock API
export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const generateId = () => 'mock-' + Math.random().toString(36).substr(2, 9);

export const simulateApiCall = async (data, delayMs = 500) => {
  await delay(delayMs);
  
  // Simulate occasional API errors (5% chance) - DISABLED during development
  // if (Math.random() < 0.05) {
  //   throw new Error('Network error: Unable to connect to server');
  // }
  
  return {
    success: true,
    data,
    metadata: {
      timestamp: new Date().toISOString(),
      requestId: generateId()
    }
  };
};

// Export all mock data as a single object for easy access
export const mockData = {
  users: mockUsers,
  hooks: mockHooks,
  sections: mockSections,
  signatures: mockSignatures,
  drafts: mockDrafts,
  brokerages: mockBrokerages,
  campaigns: mockCampaigns,
  analytics: mockAnalytics,
  abTestResults: mockABTestResults,
  realTimeMetrics: mockRealTimeMetrics,
  tasks: mockTasks
};